import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useSubmitCheckout, {
	CHECKOUT_ERROR_TYPES,
	ICheckoutError,
} from "../../apis/useSubmitCheckout/useSubmitCheckout";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { usePaymentActions, usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { addDatadogAction } from "../../services/datadog";
import Condition from "../Conditional/Condition";
import PopupDialog from "../elements/PopupDialog/PopupDialog";
import "./CheckoutButton.scss";

interface CheckoutButtonProps {
	className?: string;
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({ className }) => {
	const { t } = useTranslation();
	const {
		state: { cart },
	} = useCartContext((state) => state);
	const paymentActions = usePaymentActions();
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const navigate = useNavigate();

	const {
		submitCheckout,
		isLoading: isSubmittingCheckout,
		error,
		removeOosItems,
		oosItemNames,
	} = useSubmitCheckout();
	const [showErrorPopup, setShowErrorPopup] = useState(false);
	const [errorAttemptCount, setErrorAttemptCount] = useState(0);
	const isDisabled = cart.length === 0 || isSubmittingCheckout;

	const debouncedSubmit = useMemo(
		() =>
			debounce(async () => {
				submitCheckout();
			}, 300),
		[submitCheckout]
	);

	const handleClickCheckout = useCallback(() => {
		setShowErrorPopup(false);
		debouncedSubmit();
	}, [debouncedSubmit]);

	const handleTryAgain = useCallback(() => {
		setShowErrorPopup(false);
		debouncedSubmit();
	}, [debouncedSubmit]);

	const handleCloseError = useCallback(() => {
		setShowErrorPopup(false);
		setErrorAttemptCount(0);
	}, []);

	useEffect(() => {
		addDatadogAction("checkout_button_clear_on_mount", {
			hadPaymentReference: !!paymentReference,
		});
		paymentActions.CLEAR_PAYMENT_DATA();
	}, []);

	useEffect(() => {
		if (paymentReference && paymentReference.length > 0 && isSubmittingCheckout === false) {
			addDatadogAction("checkout_button_navigate_to_payment", {
				isSubmittingCheckout,
				paymentReferencePrefix: paymentReference?.substring(0, 7) ?? null,
			});
			navigate(PATH.PAYMENT);
		} else {
			console.info(
				"paymentRef:",
				paymentReference?.substring(0, 7),
				"\nisSubmittingCheckout:",
				isSubmittingCheckout
			);
		}
	}, [paymentReference, navigate, isSubmittingCheckout]);

	const handleRemoveOosItems = useCallback(() => {
		setShowErrorPopup(false);
		removeOosItems();
	}, [removeOosItems]);

	useEffect(() => {
		if (error) {
			setErrorAttemptCount((prev) => prev + 1);
			setShowErrorPopup(true);
		}
	}, [error]);

	// Type guard to check if error is ICheckoutError
	const isCheckoutError = (err: unknown): err is ICheckoutError => {
		return err !== null && typeof err === "object" && "type" in err && "items" in err;
	};

	// Memoize popup content based on error type
	const isOosError = isCheckoutError(error) && error.type === CHECKOUT_ERROR_TYPES.OOS_ERROR;

	const popupTitle = useMemo(() => {
		if (isOosError) {
			return t("cartPage.oosTitle");
		}

		return errorAttemptCount === 1 ? t("paymentPage.timeoutTitle") : t("paymentPage.failedTitle");
	}, [isOosError, errorAttemptCount, t]);

	const popupMessage = useMemo(() => {
		if (isOosError && oosItemNames.length > 0) {
			return (
				<>
					<div className="OosMessage">{t("cartPage.oosMessage")}</div>
					<div className="OosItemsList">
						{oosItemNames.map((name, index) => (
							<div key={index + name} className="OosItem">
								{name}
							</div>
						))}
					</div>
				</>
			);
		}

		return errorAttemptCount === 1
			? t("paymentPage.timeoutMessage")
			: t("paymentPage.failedMessage");
	}, [isOosError, errorAttemptCount, t, oosItemNames]);

	const popupButtons = useMemo(() => {
		if (isOosError) {
			return [
				{
					text: t("cartPage.oosRemove"),
					onClick: handleRemoveOosItems,
					className: "PrimaryButton",
				},
			];
		}

		if (errorAttemptCount === 1) {
			return [
				{
					text: t("paymentPage.tryAgain"),
					onClick: handleTryAgain,
					className: "PrimaryButton",
				},
			];
		}

		return [
			{
				text: t("paymentPage.close"),
				onClick: handleCloseError,
				className: "PrimaryButton",
			},
		];
	}, [isOosError, errorAttemptCount, t, handleTryAgain, handleCloseError, handleRemoveOosItems]);

	return (
		<>
			<button
				className={`CheckoutButton ${isDisabled ? "Disabled" : ""} ${className || ""}`}
				onClick={handleClickCheckout}
				disabled={isDisabled}
			>
				<span className="CheckoutText">
					<span className="CartTitle">{t("Cart.continueToPayment")}</span>
					<Condition check={!isSubmittingCheckout}>
						<span className="CheckoutArrow">&gt;</span>
					</Condition>
					<Condition check={isSubmittingCheckout}>
						<span className="LoadingSpinner" />
					</Condition>
				</span>
			</button>

			<PopupDialog
				data-testid="checkout-error-popup"
				open={showErrorPopup}
				title={popupTitle}
				onClose={handleCloseError}
				content={<div className="ErrorContent">{popupMessage}</div>}
				buttons={popupButtons}
				className="CheckoutErrorPopup"
			/>
		</>
	);
};

export default CheckoutButton;
